import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import Quagga from '@ericblade/quagga2';
import { useZxing } from 'react-zxing';

import { composeClassNames, convertRialToToman, pushGTMEvent } from 'Helpers';
import Icon from 'Components/Globals/Elements/Icon/loadable';
import Frame from 'Assets/images/payment/Frame.svg';
import Loading from 'Components/Globals/Loading/loadable';
import loading from 'Assets/images/payment/loading.gif';
import routes from 'Pages/router/constants/routes';
import { SAGA_REQUEST_KEY } from 'Constants';
import useServices from 'Hooks/useServices';
import useContent from 'Hooks/useContent';
import {
	generalSelectors,
	walletPaymentTypes,
	walletPaymentActions,
	walletPaymentSelectors,
} from 'Ducks';

import styles from './styles.module.scss';
import strings from './strings';

// ─── Scanner strategy constants ───────────────────────────────────────────────
const SCANNER = {
	ZXING: 'zxing',
	QUAGGA: 'quagga',
};

const FALLBACK_CHAIN = [SCANNER.ZXING, SCANNER.QUAGGA];

const isCameraPermissionDeniedError = (err) =>
	err?.name === 'NotAllowedError' || err?.name === 'PermissionDeniedError';

// ─── Camera lens selection ────────────────────────────────────────────────────
// On newer multi-camera phones (most new Samsungs) `facingMode: environment`
// often binds to the ULTRA-WIDE lens, which can't focus on close-up barcodes.
// Resolve the MAIN back lens explicitly and pin it via deviceId instead.
const getMainBackCameraId = async () => {
	try {
		// Request permission first so enumerateDevices() returns labels.
		const probe = await navigator.mediaDevices.getUserMedia({
			video: { facingMode: 'environment' },
		});
		probe.getTracks().forEach((track) => track.stop());

		const cams = (await navigator.mediaDevices.enumerateDevices()).filter(
			(device) => device.kind === 'videoinput'
		);
		const backCams = cams.filter((device) =>
			/back|rear|environment/i.test(device.label)
		);
		// Prefer a lens that is NOT ultra-wide/tele/depth/macro (the main sensor).
		const main =
			backCams.find(
				(device) => !/wide|ultra|tele|depth|macro/i.test(device.label)
			) ||
			backCams[0] ||
			cams[0];

		return { deviceId: main?.deviceId, permissionDenied: false };
	} catch (err) {
		return {
			deviceId: undefined,
			permissionDenied: isCameraPermissionDeniedError(err),
		};
	}
};

// ─── Camera constraints (shared across all scanners) ─────────────────────────
// Pins the resolved main lens when available; falls back to facingMode otherwise.
const buildVideoConstraints = (deviceId) => ({
	deviceId: deviceId ? { exact: deviceId } : undefined,
	facingMode: deviceId ? undefined : 'environment',
	width: { ideal: 1280 },
	height: { ideal: 720 },
	zoom: 2,
	advanced: [{ focusMode: 'continuous' }],
});

// ─── ZXing sub-component (mounted/unmounted based on activeScanner) ───────────
// Isolated so useZxing hook only runs when ZXing is the active scanner.
const ZXingScanner = ({ onDetected, onError, deviceId }) => {
	const { ref } = useZxing({
		onDecodeResult: (result) => onDetected(result.getText()),
		onError,
		timeBetweenDecodingAttempts: 300,
		constraints: { video: buildVideoConstraints(deviceId) },
	});

	return (
		<video
			ref={ref}
			className={styles.wrapper__camera}
			muted
			autoPlay
			playsInline
		/>
	);
};

// ─── Main component ───────────────────────────────────────────────────────────
const BarcodeScanner = () => {
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const { formatMessage } = useContent();
	const { business } = useServices();

	// null = not started yet; set to first FALLBACK_CHAIN entry on mount
	const [activeScanner, setActiveScanner] = useState(null);
	const [insufficientBox, setInsufficientBox] = useState(false);
	const amount = useSelector(walletPaymentSelectors.selectAmount) ?? 0;

	const videoRef = useRef(null); // used by Quagga
	const hasScannedRef = useRef(false); // synchronous guard against duplicate dispatches
	const deviceIdRef = useRef(undefined); // resolved main back-lens deviceId
	const permissionDeniedTrackedRef = useRef(false);

	const id = useSelector(walletPaymentSelectors.selectTransactionId);
	const error = useSelector(walletPaymentSelectors.selectBarcodeError);
	const isLoading = useSelector((state) =>
		generalSelectors.selectApiLoadingStatus(
			state,
			walletPaymentTypes.CHECK_BARCODE.REQUEST
		)
	);

	const trackCameraPermissionDenied = useCallback(() => {
		if (permissionDeniedTrackedRef.current) return;
		permissionDeniedTrackedRef.current = true;
		pushGTMEvent('wallet_barcode_camera_submit_permission_denied', {
			vendor_name: business?.name || '',
			vendor_id: business?.v3_id || '',
		});
	}, [business]);

	// ── Dispatch scanned barcode to saga ──────────────────────────────────────
	const handleBarcodeScan = useCallback(
		(barcode) => {
			if (!barcode || hasScannedRef.current) return;
			hasScannedRef.current = true;
			dispatch(
				walletPaymentActions.checkBarcodeRequest({
					id,
					barcode,
					navigate,
					business,
					successEventName: 'wallet_barcode_camera_submit_success',
					failedEventName: 'wallet_barcode_camera_submit_invalid_barcode_error',
					[SAGA_REQUEST_KEY.DISABLE_TOAST]: true,
				})
			);
		},
		[dispatch, id, navigate, business]
	);

	// ── Stop everything currently running ────────────────────────────────────
	// Called both on unmount and before switching to the next scanner.
	const cleanupAll = useCallback(() => {
		// Remove any previously registered Quagga listener before stopping,
		// so stacked listeners don't fire on the next init.
		try {
			Quagga.offDetected();
			Quagga.stop();
		} catch (_) {}
	}, []);

	// ── Advance to next scanner in chain ─────────────────────────────────────
	const tryNextScanner = useCallback((failedScanner) => {
		const next = FALLBACK_CHAIN[FALLBACK_CHAIN.indexOf(failedScanner) + 1];
		if (next) {
			console.warn(
				`[BarcodeScanner] "${failedScanner}" failed → trying "${next}"`
			);
			setActiveScanner(next);
		} else {
			console.error('[BarcodeScanner] All scanners exhausted.');
		}
	}, []);

	// ── Quagga ────────────────────────────────────────────────────────────────
	const startQuaggaScanner = useCallback(() => {
		if (!videoRef.current) {
			tryNextScanner(SCANNER.QUAGGA);
			return;
		}
		Quagga.init(
			{
				inputStream: {
					type: 'LiveStream',
					target: videoRef.current,
					constraints: buildVideoConstraints(deviceIdRef.current),
				},
				decoder: {
					readers: [
						'code_128_reader',
						'ean_reader',
						'ean_8_reader',
						'code_39_reader',
						'upc_reader',
						'upc_e_reader',
						'i2of5_reader',
					],
				},
				locate: true,
			},
			(err) => {
				if (err) {
					console.warn('[BarcodeScanner] Quagga init error:', err);
					if (isCameraPermissionDeniedError(err)) {
						trackCameraPermissionDenied();
					}
					tryNextScanner(SCANNER.QUAGGA);
					return;
				}
				Quagga.start();
			}
		);

		// Register after init to avoid stacking (cleanupAll calls offDetected first)
		Quagga.onDetected((result) => {
			const code = result?.codeResult?.code;
			if (code) handleBarcodeScan(code);
		});
	}, [handleBarcodeScan, tryNextScanner, trackCameraPermissionDenied]);

	// ── On mount: resolve main lens, then kick off fallback chain ─────────────
	useEffect(() => {
		let cancelled = false;
		(async () => {
			const { deviceId, permissionDenied } = await getMainBackCameraId();
			deviceIdRef.current = deviceId;
			if (permissionDenied) {
				trackCameraPermissionDenied();
			}
			if (!cancelled) setActiveScanner(FALLBACK_CHAIN[0]);
		})();
		return () => {
			cancelled = true;
			cleanupAll();
		};
	}, [cleanupAll, trackCameraPermissionDenied]);

	// ── When activeScanner changes: cleanup previous, start next ─────────────
	useEffect(() => {
		if (!activeScanner) return;

		cleanupAll();
		hasScannedRef.current = false;

		if (activeScanner === SCANNER.QUAGGA) startQuaggaScanner();
		// SCANNER.ZXING: handled declaratively — <ZXingScanner> mounts when active
	}, [activeScanner]); // eslint-disable-line react-hooks/exhaustive-deps
	// ^ cleanupAll/startQuaggaScanner are stable (useCallback with no changing
	//   deps), intentionally omitted to prevent double-firing.

	// ── Saga error response ───────────────────────────────────────────────────
	useEffect(() => {
		if (!error) return;
		if (error === formatMessage(strings.insufficient)) {
			setInsufficientBox(true);
			hasScannedRef.current = false; // allow the user to try again
		} else {
			navigate(routes.transactionStatus);
		}
	}, [error, navigate, formatMessage]);

	// ─────────────────────────────────────────────────────────────────────────
	return (
		<div className={styles.wrapper}>
			<p
				className={composeClassNames([
					'mt-16 txt-gray sm mh-16',
					styles.wrapper__description,
				])}
			>
				{formatMessage(strings.description)}
			</p>
			<div className={`bg-base p-8 m-12 ${styles.discount_wrapper}`}>
				<p className='sm txt-dark line-height-24 bold'>
					{formatMessage(strings.amount)}
				</p>
				<div className={styles.discount_amount_wrapper}>
					<p className='txt-dark md line-height-24 bold'>
						{convertRialToToman(amount).replaceAll(',', '،')}
					</p>
					<Icon
						name='toman'
						size='xs'
						className='txt-dark'
					/>
				</div>
			</div>
			{/* Status banner */}
			{insufficientBox ? (
				<div
					className={composeClassNames([
						'p-16 m-16',
						styles.wrapper__insufficient,
					])}
				>
					<img
						className={styles.wrapper__image}
						src={loading}
						alt='loading'
					/>
					<p>{formatMessage(strings.insufficient)}</p>
				</div>
			) : null}

			{/* Camera viewport */}
			<div className={styles.wrapper__camera}>
				{activeScanner === SCANNER.ZXING ? (
					<ZXingScanner
						onDetected={handleBarcodeScan}
						onError={(err) => {
							if (isCameraPermissionDeniedError(err)) {
								trackCameraPermissionDenied();
							}
							tryNextScanner(SCANNER.ZXING);
						}}
						deviceId={deviceIdRef.current}
					/>
				) : (
					// Quagga writes to this video element
					<video
						ref={videoRef}
						className={styles.wrapper__camera}
						muted
						autoPlay
						playsInline
					/>
				)}
			</div>
			<img
				className={styles.wrapper__frame}
				src={Frame}
				alt='frame'
			/>
			{/* API loading indicator */}
			{isLoading && (
				<div className={styles.wrapper__loading}>
					<Loading />
				</div>
			)}
		</div>
	);
};

export default BarcodeScanner;
